import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Slash } from "lucide-react";
import { addIgnored, listIgnored } from "@/api/ignore";
import { Page } from "@/components/page";
import { EmptyState } from "@/components/empty-state";

export function IgnorePage() {
  const qc = useQueryClient();
  const [urlsText, setUrlsText] = useState("");
  const [reason, setReason] = useState("");

  const entries = useQuery({
    queryKey: ["ignored"],
    queryFn: () => listIgnored(500),
  });

  const add = useMutation({
    mutationFn: ({ urls, reason }: { urls: string[]; reason: string | null }) =>
      addIgnored(urls, reason),
    onSuccess: (r) => {
      toast.success(`Ignored ${r.added} URL${r.added === 1 ? "" : "s"}`);
      setUrlsText("");
      setReason("");
      qc.invalidateQueries({ queryKey: ["ignored"] });
    },
  });

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const urls = urlsText.split(/\r?\n/).map((s) => s.trim()).filter(Boolean);
    if (urls.length === 0) {
      toast.error("Paste at least one URL.");
      return;
    }
    add.mutate({ urls, reason: reason.trim() || null });
  };

  const items = entries.data ?? [];

  return (
    <Page title="Ignored URLs">
      <section className="card p-4">
        <h2 className="text-sm font-semibold mb-3 flex items-center gap-2">
          <Slash className="h-4 w-4" /> Bulk add
        </h2>
        <form onSubmit={onSubmit} className="grid gap-3 sm:grid-cols-[1fr_14rem_auto] sm:items-start">
          <div>
            <label className="label">URLs (one per line)</label>
            <textarea
              className="input min-h-[6rem] font-mono text-xs"
              value={urlsText}
              onChange={(e) => setUrlsText(e.target.value)}
              placeholder={"foo.example\nbar.example"}
            />
          </div>
          <div>
            <label className="label">Reason (optional)</label>
            <input
              className="input"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="legacy import"
            />
          </div>
          <button
            type="submit"
            className="btn-primary self-end"
            disabled={add.isPending}
          >
            Add
          </button>
        </form>
      </section>

      <section className="card overflow-hidden">
        <div className="px-4 py-3 border-b border-border text-sm font-semibold flex items-center justify-between">
          <span>Recent</span>
          <span className="text-xs text-muted tabular-nums">
            {items.length.toLocaleString()} shown
          </span>
        </div>
        {items.length === 0 ? (
          <EmptyState title="Nothing ignored yet" />
        ) : (
          <div className="overflow-x-auto max-h-[60vh]">
            <table className="w-full min-w-[36rem] text-sm">
              <thead className="sticky top-0 bg-card">
                <tr className="text-xs text-muted">
                  <th className="px-4 py-2 text-left font-medium">URL</th>
                  <th className="px-4 py-2 text-left font-medium">Reason</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {items.map((e) => (
                  <tr key={e.normalized_url} className="hover:bg-subtle/40">
                    <td className="px-4 py-1.5 font-mono text-xs">{e.normalized_url}</td>
                    <td className="px-4 py-1.5 text-muted">{e.reason ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </Page>
  );
}
